import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import CollapsibleGroup from 'components/sidebar/CollapsibleGroup';
import NavLink from 'components/sidebar/NavLink';
import dataTables from 'view/dataTables';
import { dataConfig } from 'data/dataConfig';
import { prettifyDataSource, DATA_SOURCES, BIS, getAppRoute } from 'data/dataSourceConstants';
import {
	isPrimarySourcedAnchorDataSet,
	shouldHideStandaloneDataSetLink,
	getPrimarySourcedMenuConfigForDataSet,
	getPrimarySourcedMenuLabel,
	getPrimarySourcedIssueCount,
	isPrimarySourcedRouteActive,
	getEntryKey,
} from 'utils/primarySourcedDataSets';
import { isDataSetInCoveredBorough } from 'utils/dataSetBoroughCoverage';
import { buildInProgressTaskNames, isDataSetLoading } from 'utils/firstLoadTasks';

import './DataSetLinks.css';

const getSortOrder = (component) => {
	//number is the order allen wants the links in
	const rank = {
		'DOB Violations': 2,
		'ECB Violations': 1,
		'Job Applications': 7,
		'Work Applications': 7,
		'Work Application': 7,
		'Work Application (BIS)': 7,
		'Work Application (DOB Now)': 7.1,
		'Electrical-(OPEN_DATA)': 9,
		'Elevator-(OPEN_DATA)': 9.1,
		'Elevator Records (BIS)-(BIS)': 9.15,
		'Boilers-(OPEN_DATA)': 9.2,
		'Boilers (BIS)-(BIS)': 9.25,
		'Board of Standards and Appeals Applications-(OPEN_DATA)': 11,
		'CityPay Tickets-(CITYPAY)': 6,
		'Sign Application Filings-(OPEN_DATA)': 11.5, //added to make it appear next to others in the same category
		'Tax Lien Sales-(OPEN_DATA)': 1300,
		'ACRIS Legals-(OPEN_DATA)': 1200,
		'ACRIS Documents-(OPEN_DATA)': 1201,
		'ACRIS Sales and Transfers-(OPEN_DATA)': 1202,
		Complaints: 5,
		'Department of Buildings': 8,
		HPD: 4,
		FDNY: 3,
	};

	//if something is missing, just put it towards the bottom
	if (!component?.key) {
		return 1000;
	}
	return rank[component.key] || 1000;
};

const linkComparator = (a, b) => getSortOrder(a) - getSortOrder(b);

const countDataSources = (dataSetConfig) => {
	let dataSourceCount = 0;
	DATA_SOURCES.forEach((dataSource) => {
		if (dataSetConfig[dataSource]) {
			dataSourceCount += 1;
		}
	});
	return dataSourceCount;
};

function DataSetLinks(props) {
	let { address } = useParams();
	let location = useLocation();
	let pathParts = location?.pathname?.split('/') || [];
	let currentDataSetRoute = pathParts[3];

	let propertyData = useSelector((state) => state.property.propertyData || {});
	let firstLoadTasksData = useSelector((state) => state.firstLoadTasks?.data);
	let inProgressTaskNames = buildInProgressTaskNames(firstLoadTasksData);

	let navItems = [];
	let groupings = {};
	let groupingCategories = {};
	let groupingSums = {};
	let groupingIsLoading = {};
	let activeGroup = null;

	//data set loop
	Object.keys(dataTables).forEach((dataSet) => {

		// A data set that does not cover this property's borough is hidden outright,
		// rather than linking to a table that can only ever be empty - which would
		// read as "searched and found nothing" instead of "not covered here".
		if (!isDataSetInCoveredBorough(dataSet, propertyData)) {
			return;
		}
		if (shouldHideStandaloneDataSetLink(dataSet)) {
			return;
		}

		let config = dataTables[dataSet];

		if (config.sidebarOnly) {
			return;
		}

		let title = config.navTitle || config.title;
		let groupName = dataTables[dataSet].grouping;
		let category = dataTables[dataSet].dataCategory;
		let isViolation = !!config.isViolation;
		let dataSourceCount = countDataSources(config);
		let multiSourceLinks = []; //multiSource = when a dataset has open-data and bis sources
		let multiSourceViolationCounts = [];
		let bisViolationCount = 0;
		let hasBIS = false;
		let hasActiveChild = false;

		if (isPrimarySourcedAnchorDataSet(dataSet)) {
			const menuConfig = getPrimarySourcedMenuConfigForDataSet(dataSet);
			const menuLabel = getPrimarySourcedMenuLabel(menuConfig);
			const violationCount = getPrimarySourcedIssueCount(menuConfig, propertyData);
			const isActive = isPrimarySourcedRouteActive(dataSet, currentDataSetRoute);
			const subLinks = menuConfig.entries.map((entry) => {
				const dataSetRoute = getAppRoute(entry.dataSet, entry.dataSource);
				const openIssueCountField =
					dataConfig[entry.dataSet]?.openIssueCountFields?.[entry.dataSource];
				const openIssueCount = propertyData[openIssueCountField] || 0;
				const isEntryActive = dataSetRoute === currentDataSetRoute;
				return (
					<NavLink
						isViolation={isViolation}
						category={category}
						key={getEntryKey(entry.dataSet, entry.dataSource)}
						isActive={isEntryActive}
						url={`/property/${address}/${dataSetRoute}`}
						label={entry.label}
						violationCount={openIssueCount}
					/>
				);
			});

			navItems.push(
				<CollapsibleGroup
					category={category}
					key={menuLabel}
					label={menuLabel}
					violationCount={violationCount}
					hasActiveChild={isActive}
					subLinks={subLinks}
				/>
			);
			return;
		}

		let isMultiSourceLoading = false;

		//data source sub-loop
		DATA_SOURCES.forEach((dataSource) => {
			if (config[dataSource]) {
				let prettyTitle = title;
				let openIssueCount = 0;
				let openIssueCountField =
					dataConfig[dataSet]?.openIssueCountFields[dataSource];
				let isLoading = isDataSetLoading(dataSet, dataSource, inProgressTaskNames);
				let dataSetRoute = getAppRoute(dataSet, dataSource);
				let isActive = dataSetRoute === currentDataSetRoute;
				hasActiveChild = isActive || hasActiveChild;
				if (dataSourceCount > 1) {
					let prettyDataSource = prettifyDataSource(dataSource);
					prettyTitle = `${title} (${prettyDataSource})`;
				}

				// console.log(dataSet, dataSource, config);

				//only show open issues if the data set is some type of violation (disabled)
				if (openIssueCountField /* && isViolation*/) {
					openIssueCount = propertyData[openIssueCountField];
					if (dataSource === BIS) {
						hasBIS = true;
						bisViolationCount = parseInt(openIssueCount);
					}
				}

				if (groupName) {
					//custom grouping
					if (isActive) {
						activeGroup = groupName;
					}
					groupingCategories[groupName] = category;
					if (isLoading) {
						groupingIsLoading[groupName] = true;
					}
					if (groupings[groupName]) {
						groupingSums[groupName] += parseInt(openIssueCount);
						groupings[groupName].push(
							<NavLink
								isViolation={isViolation}
								category={category}
								key={`${dataSet}-${dataSource}`}
								isActive={isActive}
								url={`/property/${address}/${dataSetRoute}`}
								label={prettyTitle}
								violationCount={openIssueCount}
								isLoading={isLoading}
							/>,
						);
					} else {
						groupingSums[groupName] = openIssueCount;
						let btn = (
							<NavLink
								isViolation={isViolation}
								category={category}
								key={`${dataSet}-${dataSource}`}
								isActive={isActive}
								url={`/property/${address}/${dataSetRoute}`}
								label={prettyTitle}
								violationCount={openIssueCount}
								isLoading={isLoading}
							/>
						);
						groupings[groupName] = [btn];
					}
				} else if (dataSourceCount === 1) {
					//ungrouped top level dataset, single data source
					navItems.push(
						<li key={`${title}-(${dataSource})`}>
							<NavLink
								isViolation={isViolation}
								category={category}
								isActive={isActive}
								url={`/property/${address}/${dataSetRoute}`}
								label={prettyTitle}
								violationCount={openIssueCount}
								isLoading={isLoading}
							/>
						</li>,
					);
				} else if (dataSourceCount > 1) {
					//auto-grouping for multi data source: open-data + bis
					multiSourceViolationCounts.push(parseInt(openIssueCount));
					if (isLoading) {
						isMultiSourceLoading = true;
					}
					multiSourceLinks.push(
						<NavLink
							isViolation={isViolation}
							category={category}
							key={prettyTitle}
							isActive={isActive}
							url={`/property/${address}/${dataSetRoute}`}
							label={prettyTitle}
							violationCount={openIssueCount}
							isLoading={isLoading}
						/>,
					);
				}
			}
		});

		if (multiSourceLinks.length > 1) {
			let isEqualVioCounts = multiSourceViolationCounts.every(
				(n) => n === multiSourceViolationCounts[0],
			);
			let violationCount = isEqualVioCounts
				? multiSourceViolationCounts[0]
				: '?';
			if (hasBIS) {
				//always use bis count, if available. even if multiple counts don't reconcile
				violationCount = bisViolationCount;
			}
			navItems.push(
				<CollapsibleGroup
					category={category}
					key={title}
					label={title}
					violationCount={violationCount}
					hasActiveChild={hasActiveChild}
					subLinks={multiSourceLinks}
					isLoading={isMultiSourceLoading}
				/>,
			);
		}
	});

	Object.keys(groupings).forEach((groupName) => {
		let isActiveGroup = groupName === activeGroup;
		navItems.push(
			<CollapsibleGroup
				category={groupingCategories[groupName]}
				key={groupName}
				label={groupName}
				violationCount={groupingSums[groupName]}
				hasActiveChild={isActiveGroup}
				subLinks={groupings[groupName]}
				isLoading={!!groupingIsLoading[groupName]}
			/>,
		);
	});

	navItems.sort(linkComparator);
	return navItems;
}

export default DataSetLinks;
